import { motion } from "framer-motion";
import {
  BrainCircuit,
  Radio,
  Cloud,
  Cpu,
  CheckCircle2,
} from "lucide-react";

const services = [
  {
    icon: BrainCircuit,
    title: "AI Solutions",
    desc: "From Generative AI to autonomous agents — production-ready intelligence for your business.",
    items: [
      "Generative AI",
      "AI Agents",
      "Computer Vision",
      "Predictive Analytics",
      "NLP & Chatbots",
      "RAG Applications",
    ],
    accent: "from-blue-500 to-cyan-400",
  },
  {
    icon: Radio,
    title: "IoT Solutions",
    desc: "Connect, monitor and automate any asset — from factory floor to field deployment.",
    items: [
      "Smart Sensors",
      "Device Connectivity",
      "Remote Monitoring",
      "Asset Tracking",
      "Energy Monitoring",
      "Industrial Automation",
    ],
    accent: "from-cyan-400 to-teal-400",
  },
  {
    icon: Cloud,
    title: "Cloud & DevOps",
    desc: "Cloud-native, container-first platforms engineered for scale, speed and reliability.",
    items: [
      "Kubernetes",
      "Cloud Migration",
      "CI/CD Automation",
      "Monitoring & Observability",
      "Infrastructure as Code",
    ],
    accent: "from-indigo-500 to-blue-500",
  },
  {
    icon: Cpu,
    title: "Edge Computing",
    desc: "Push intelligence to the edge for ultra-low-latency analytics and offline resilience.",
    items: [
      "Edge AI",
      "Real-time Analytics",
      "Local Processing",
      "Edge Device Management",
    ],
    accent: "from-fuchsia-500 to-cyan-400",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-3">
            What We Build
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            End-to-end services for the
            <span className="gradient-text-vivid"> intelligent enterprise</span>
          </h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg">
            One partner. Four practices. Production-grade outcomes.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="card-hover relative bg-[#131C2F] border border-white/10 rounded-2xl p-6 lg:p-7 overflow-hidden"
              >
                <div className={`absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br ${s.accent} opacity-10 rounded-full blur-3xl`} />
                <div className="relative">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.accent} grid place-items-center shadow-lg shadow-cyan-500/20`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
